/**
 * Checkbox Input Component
 * =======================
 * 
 * Form input template component for rendering multiple checkbox options with validation.
 * This component is designed to be used within individual form components to provide
 * a consistent checkbox interface with support for multiple selections and validation.
 * 
 * Core Features:
 * - Multiple checkbox options with configurable layout
 * - Responsive grid layout (1, 2, or 3 columns)
 * - Form validation integration with error display
 * - Custom styling for square checkboxes
 * - Support for minimum/maximum selection requirements
 * - Responsive design for mobile devices
 * 
 * Input Properties:
 * - templateFormGroup: FormGroup containing the checkbox controls
 * - checkboxes: Array of checkbox options with name and label
 * - label: Main field label with required indicator
 * - columns: Number of columns for grid layout (1-3)
 * - editable: Whether checkboxes can be modified
 * 
 * Validation Features:
 * - Required field validation
 * - Minimum/maximum selection validation
 * - Visual error states and messages
 * - Touch state management for validation display
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { checkboxValidator } from '../../../../../utils/form-validators';

@Component({
  selector: 'app-checkbox-input',
  template: `
    <ng-container *ngIf="templateFormGroup && checkboxes?.length" [formGroup]="templateFormGroup">
      <ion-label [position]="position" class="form-label">
        <strong>{{ label }}:</strong>
        <ion-text *ngIf="requiredField" color="danger">*</ion-text>
      </ion-label>

      <div class="checkbox-group" [class]="'columns-' + columns">
        <ion-item *ngFor="let checkbox of checkboxes" lines="none">
          <ion-checkbox
            [formControlName]="checkbox.name"
            [labelPlacement]="labelPlacement"
            [disabled]="disabled"
            [class.ion-invalid]="templateFormGroup.get(checkbox.name)?.invalid && templateFormGroup.get(checkbox.name)?.touched"
            [class.ion-touched]="templateFormGroup.get(checkbox.name)?.touched">
            <div class="ion-text-wrap">{{ checkbox.label }}</div>
          </ion-checkbox>
        </ion-item>
      </div>
  
      <div *ngIf="templateFormGroup.invalid  && templateFormGroup.touched" class="error-container">
        <ion-text color="danger">
          <small *ngIf="templateFormGroup.errors?.['minSelections']">
            Please select at least {{ templateFormGroup.errors?.['minSelections']?.required }} options (currently selected: {{ templateFormGroup.errors?.['minSelections']?.actual }})
          </small>
          <small *ngIf="templateFormGroup.errors?.['maxSelections']">
            You can only select up to {{ templateFormGroup.errors?.['maxSelections']?.required }} options (currently selected: {{ templateFormGroup.errors?.['maxSelections']?.actual }})
          </small>
        </ion-text>
      </div>
    </ng-container>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
    }

    ion-checkbox::part(container) {
  width: 30px;
  height: 30px;
  border-radius: 0; /* Makes it square - change from default circular */
  border: 2px solid #ddd;
}



    .checkbox-group {
      margin-top: 10px;
    }

    .checkbox-group.columns-1 {
      display: block;
    }

    .checkbox-group.columns-2 {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem
    }

    .checkbox-group.columns-3 {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 1rem;
    }

    ion-item {
      --padding-start: 0;
      --inner-padding-end: 0;
    }

    ion-checkbox {
      margin-right: 8px;
      white-space: normal;
      word-wrap: break-word;
      word-break: break-word;
      overflow-wrap: break-word;
      max-width: 100%;
      display: inline-block;
      vertical-align: middle;
    }

    .error-container {
      margin-top: 4px;
      margin-left: 16px;
    }

    .error-container small {
      display: block;
      font-size: 0.75rem;
      line-height: 1.2;
    }

    ion-checkbox.ion-invalid.ion-touched {
      --border-color: var(--ion-color-danger);
      --color: var(--ion-color-danger);
    }

    /* Responsive adjustments for smaller screens */
    @media (max-width: 768px) {
      .checkbox-group.columns-2,
      .checkbox-group.columns-3 {
        grid-template-columns: 1fr;
        gap: 4px;
      }
    }
  `]
})
export class CheckboxInputComponent implements OnInit, OnChanges {
  @Input() templateFormGroup!: FormGroup;
  @Input() checkboxes: { name: string, label: string }[] = [];
  @Input() label: string = '';
  @Input() labelPlacement: 'start' | 'end' | 'fixed' |'stacked' = 'end';
  @Input() requiredField: boolean = false;
  @Input() disabled: boolean = false;
  @Input() position: 'stacked' | 'fixed' | 'floating' = 'floating';
  @Input() columns: number = 1;
 

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Initializes the form group with checkbox controls.
   */
  ngOnInit(): void {
    this.initializeFormGroup();
  }

  /**
   * Lifecycle hook that responds to input property changes.
   * Reinitializes the form group when templateFormGroup or checkboxes change.
   * 
   * @param changes - Object containing information about changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['templateFormGroup'] || changes['checkboxes'] || changes['requiredField'] || changes['disabled']) {
      this.initializeFormGroup();
      // If only the disabled flag toggled and no new controls were added, still apply the state
      if (changes['disabled'] && !changes['checkboxes'] && !changes['templateFormGroup']) {
        this.applyDisabledState();
      }
    }
  }

  /**
   * Initializes the form group by adding missing checkbox controls.
   * Ensures all checkbox options have corresponding form controls.
   * Only runs when both templateFormGroup and checkboxes are available.
   * 
   * @private
   */
  private initializeFormGroup(): void {
    if (!this.templateFormGroup || !this.checkboxes?.length) return;

    // Add any missing controls to the form group
    for (const checkbox of this.checkboxes) {
      if (!this.templateFormGroup.get(checkbox.name)) {
        this.templateFormGroup.addControl(checkbox.name, new FormControl(false));
      }
    }
    // Ensure disabled state is reflected at the FormControl level (Angular reactive forms override [disabled])
    this.applyDisabledState();
  }

  /**
   * Applies the disabled/enabled state from the @Input() to each checkbox FormControl.
   * This is required because with reactive forms, the FormControl's disabled state
   * takes precedence over an element-level [disabled] binding. Without this, Ionic's
   * checkbox remains interactive even if [disabled] is set on the component.
   */
  private applyDisabledState(): void {
    if (!this.templateFormGroup) return;
    for (const checkbox of this.checkboxes) {
      const ctrl = this.templateFormGroup.get(checkbox.name);
      if (!ctrl) continue;
      if (this.disabled && ctrl.enabled) {
        ctrl.disable({ emitEvent: false });
      } else if (!this.disabled && ctrl.disabled) {
        ctrl.enable({ emitEvent: false });
      }
    }
  }
}
